import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AsyncReduxState, initialAsyncReduxState } from '../../../redux/@config/AsyncReduxState'
import { BaseScheduleDayComment, BaseScheduleDetail, BaseScheduleIndex } from '../../entities/BaseSchedule/BaseSchedule'
import { baseScheduleShiftSlice } from '../baseScheduleShift/baseScheduleShiftReducer'

export type BaseScheduleState = AsyncReduxState<{
    baseSchedule?: BaseScheduleDetail
    baseSchedules: BaseScheduleIndex[]
    isEditSuccessful: boolean
    lastModifiedBaseScheduleShiftId?: string
    selectedWeekIndex: number
}>;

const initialState: BaseScheduleState = {
    ...initialAsyncReduxState,
    baseSchedules: [],
    isEditSuccessful: false,
    selectedWeekIndex: 0,
}

export const baseScheduleSlice = createSlice({
    name: 'baseScheduleReducer',
    initialState,
    reducers: {
        setBaseSchedule(state, action: PayloadAction<BaseScheduleDetail | undefined>): BaseScheduleState {
            return {
                ...state,
                baseSchedule: action.payload,
            }
        },
        setBaseSchedules(state, action: PayloadAction<BaseScheduleIndex[]>): BaseScheduleState {
            return {
                ...state,
                baseSchedules: action.payload,
            }
        },
        removeBaseScheduleFromIndex(state, action: PayloadAction<string>): BaseScheduleState {
            return {
                ...state,
                baseSchedules: state.baseSchedules.filter(baseSchedule => baseSchedule.id !== action.payload),
            }
        },
        setIsLoading(state, action: PayloadAction<boolean>): BaseScheduleState {
            return {
                ...state,
                isLoading: action.payload,
            }
        },
        setIsSuccessful(state, action: PayloadAction<boolean>): BaseScheduleState {
            return {
                ...state,
                isSuccessful: action.payload,
            }
        },
        setIsEditSuccessful(state, action: PayloadAction<boolean>): BaseScheduleState {
            return {
                ...state,
                isEditSuccessful: action.payload,
            }
        },
        setError(state, action: PayloadAction<string>): BaseScheduleState {
            return {
                ...state,
                error: action.payload,
            }
        },
        setLastModifiedBaseScheduleShiftId(state, action: PayloadAction<string | undefined>): BaseScheduleState {
            return {
                ...state,
                lastModifiedBaseScheduleShiftId: action.payload,
            }
        },
        setSelectedWeekIndex(state, action: PayloadAction<number>): BaseScheduleState {
            return {
                ...state,
                selectedWeekIndex: action.payload,
            }
        },
        baseScheduleUpdated(state, action: PayloadAction<Partial<BaseScheduleDetail>>): BaseScheduleState {
            return {
                ...state,
                baseSchedule: state.baseSchedule && {
                    ...state.baseSchedule,
                    ...action.payload,
                },
                baseSchedules: state.baseSchedules.map(baseSchedule => (
                    baseSchedule.id === action.payload.id
                        ? {
                            ...baseSchedule,
                            ...action.payload,
                        }
                        : baseSchedule
                )),
            }
        },
        commentAdded(state, action: PayloadAction<BaseScheduleDayComment>): BaseScheduleState {
            if (! state.baseSchedule) {
                return state
            }

            return {
                ...state,
                baseSchedule: {
                    ...state.baseSchedule,
                    dayComments: [
                        ...state.baseSchedule.dayComments,
                        action.payload,
                    ],
                },
            }
        },
        commentDeleted(state, action: PayloadAction<string>): BaseScheduleState {
            if (! state.baseSchedule) {
                return state
            }

            return {
                ...state,
                baseSchedule: {
                    ...state.baseSchedule,
                    dayComments: state.baseSchedule.dayComments.filter(comment => comment.id !== action.payload),
                },
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(baseScheduleShiftSlice.actions.setBaseScheduleShift, (state, action): BaseScheduleState => {
            if (! state.baseSchedule || ! action.payload) {
                return state
            }

            const updatedBaseSchedule: BaseScheduleDetail = {
                ...state.baseSchedule,
                shifts: [
                    ...state.baseSchedule.shifts.filter(shift => shift.id !== action.payload!.id),
                    action.payload,
                ],
            }

            return {
                ...state,
                baseSchedule: updatedBaseSchedule,
            }
        })
    },
})

export const {
    setBaseSchedule,
    setBaseSchedules,
    removeBaseScheduleFromIndex,
    setIsLoading,
    setError,
    setIsSuccessful,
    setIsEditSuccessful,
    setLastModifiedBaseScheduleShiftId,
    setSelectedWeekIndex,
    baseScheduleUpdated,
    commentAdded,
    commentDeleted,
} = baseScheduleSlice.actions

export default baseScheduleSlice.reducer
